import { defineStore } from "pinia";

import type { SubscriptionState } from "@/Modules/Subscription/Model/Store";
import type { PlanUi } from "../Plan/Models/Ui/PlanUi";
import type { SubscribeUiQuery, Subscription } from "@/Modules/Models/Domain/Subscription";
import type { Plan } from "@/Modules/Models/Domain/Plan";
import type { CancelSubscriptionApiQuery } from "@/Modules/Models/Api/Subscription";
import { SubscriptionStatus, SubscriptionStateId } from "@/Modules/Models/Domain/Subscription";
import { StateNamespace } from "@/Modules/App/Models/State/StateNamespace";

import GetSubscriptionActionHandler from "@/Modules/Subscription/ActionHandlers/GetSubscription";
import SubscribeToPlanActionHandler from "@/Modules/Subscription/ActionHandlers/SubscribeToPlan";

import { useUserStore } from "@/Modules/User/Store";
import usePlanStore from "@/Modules/Plan/Store";

import EndTrialPeriodActionHandler from "@/Modules/Subscription/ActionHandlers/EndTrialPeriod";
import CancelSubscriptionActionHandler from "@/Modules/Subscription/ActionHandlers/CancelSubscription";
import UpdateSubscriptionStatusActionHandler from "@/Modules/Subscription/ActionHandlers/UpdateSubscriptionStatus";

import {
  subscriptionGaEvent,
  EventAction,
  EventCategory,
  addGtmEvent,
} from "@/Modules/App/TrackersAndCookies/GoogleGTM";

import { hasValue, keysOf } from "@/Utils/Objects";
import { isValidString } from "@/Utils/String";

const defaultState: SubscriptionState = {
  subscription: null,
  isLoading: false,
  notification: null,
};

export const useSubscriptionStore = defineStore(StateNamespace.SUBSCRIPTION, {
  state: () => ({
    ...defaultState,
    userStore: useUserStore(),
    planStore: usePlanStore(),
  }),
  getters: {
    getPlanFromSubscription(state): PlanUi | undefined {
      const subscription = state.subscription;

      if (subscription && hasValue(subscription.planUuid)) {
        const planId = subscription.isPromo ? subscription.nextPlanUuid : subscription.planUuid;

        return this.planStore.getPlanUiById(planId);
      }
    },

    subscriptionState(state): SubscriptionStateId | number | undefined {
      return hasValue(state.subscription?.subscriptionState) ? this.subscription?.subscriptionState.id : -1;
    },

    isTheSubscriptionWrong(state): boolean | undefined {
      if (state.subscription && hasValue(state.subscription.status)) {
        return (
          hasValue(state.subscription.planUuid) &&
          !(
            state.subscription.status === SubscriptionStatus.ACTIVE ||
            state.subscription.status === SubscriptionStatus.CANCELED
          )
        );
      }
    },

    getTotalPendingNotifications(state): number {
      let total = 0;

      if (state.subscription && hasValue(state.subscription.status)) {
        total += this.isTheSubscriptionWrong ? 1 : 0;
      }

      return total;
    },
    getNextPlanUuid(state): string | undefined {
      if (state.subscription && hasValue(state.subscription.nextPlanUuid)) {
        return state.subscription.nextPlanUuid;
      }
    },

    hasSubscription(state): boolean {
      return (
        this.subscriptionState !== SubscriptionStateId.PENDING_RENEW && hasValue(state.subscription?.planUuid)
      );
    },
  },
  actions: {
    clear() {
      keysOf(defaultState).forEach((k) => {
        // @ts-expect-error clear store
        this[k] = defaultState[k];
      });
    },

    async setSubscriptions() {
      if (hasValue(this.userStore.user)) {
        const userId = this.userStore.user.id;
        const getSubscriptionsActionHandler = new GetSubscriptionActionHandler(this);

        await getSubscriptionsActionHandler.handle(userId);

        if (this.subscription) {
          await this.planStore.setCurrentPlan(this.subscription.planUuid);
        }
      }
    },

    async subscribeToPlan(planId: string) {
      if (hasValue(this.userStore.user)) {
        const userId = this.userStore.user.id;
        const subscribeToPlanActionHandler = new SubscribeToPlanActionHandler(this);
        const plan = this.planStore.plans.find((plan) => plan.id === planId);
        const subscribeUiQuery: SubscribeUiQuery = {
          planId,
          userId,
          promoPlanId: plan && plan.promoPlanUuid ? plan.promoPlanUuid : null,
        };

        const subscription = await subscribeToPlanActionHandler.handle(subscribeUiQuery);

        if (subscription && plan) {
          this.sendSubscriptionAnalyticEvents(plan, subscription, userId);
        }
      }
    },

    sendSubscriptionAnalyticEvents(plan: Plan, subscription: Subscription, userId: string) {
      subscriptionGaEvent({
        transactionId: subscription.uuid,
        transactionTotal: plan.sessionPrice,
        transactionProducts: [
          {
            sku: plan.id,
            name: plan.title,
            price: plan.price,
            quantity: 1,
          },
        ],
        leadNumber: userId,
        userId: userId,
      });

      addGtmEvent({
        eventCategory: EventCategory.PRODUCTS,
        eventAction: plan.promoPlanUuid ? EventAction.START_TRIAL : EventAction.PURCHASE,
        eventLabel: plan.title,
        eventValue: plan.price,
        leadNumber: userId,
        userId: userId,
      });
    },

    async endTrialPeriod() {
      this.notification = null;

      if (hasValue(this.userStore.user)) {
        const userId = this.userStore.user.id;
        const endTrialPeriodActionHandler = new EndTrialPeriodActionHandler(this);

        await endTrialPeriodActionHandler.handle(userId);
        await this.setSubscriptions();
      }
    },

    async cancelSubscription(reason: string, comment?: string) {
      this.notification = null;

      if (this.userStore.user) {
        const uuid = this.userStore.user.id || "";
        const cancelSubscriptionActionHandler = new CancelSubscriptionActionHandler(this);

        const params: CancelSubscriptionApiQuery = {
          user_uuid: uuid,
          cancel_reason_i18n: reason,
        };

        if (isValidString(comment)) params.cancel_comment = comment;

        await cancelSubscriptionActionHandler.handle(params);
      }
    },

    async updateSubscriptionStatus() {
      this.notification = null;

      if (this.subscription) {
        const uuid = this.subscription.uuid || "";
        const updateSubscriptionStatusActionHandler = new UpdateSubscriptionStatusActionHandler(this);

        await updateSubscriptionStatusActionHandler.handle(uuid);
      }
    },
  },
});

export type SubscriptionStore = ReturnType<typeof useSubscriptionStore>;

export default useSubscriptionStore;
